import { ChangeEvent, ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import gql from 'graphql-tag';
import { InputMaskChangeEvent } from 'primereact/inputmask';
import { SearchBar } from '@/components/Common';
import { Modal } from '@/components/Layout';
import { ModalPositions } from '@/components/Layout/Modal/Modal.types';
import {
  ClassRessoucesStudentStatus,
  ProgressionStatus,
  StudentSortBy,
} from '@/api/gql/graphql';
import type {
  GetStudentDetailByClassInput,
  GetStudentsByClassInput,
} from '@/api/gql/graphql';
import { graphqlFetch } from '@/utils/graphqlFetch';

type Person = {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
};

type StudentItem = {
  _id?: string | null;
  blockedAtSomeLessons?: boolean | null;
  completedLessons: number;
  email?: string | null;
  firstName?: string | null;
  gender?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  points: number;
  status?: ClassRessoucesStudentStatus | null;
  totalLessons: number;
};

type StudentDetail = {
  _id?: string | null;
  birthDate?: string | null;
  classRanking: number;
  disciplines: Array<{
    completedLessons: number;
    discipline: { _id: string; name: string };
    points: number;
    ranking: number;
    teacher?: Person | null;
    totalLessons: number;
  }>;
  firstName: string;
  gender?: string | null;
  lastName: string;
  phoneNumber: string;
  points: number;
  progressions: Array<{
    lastEvaluationDate?: string | null;
    lessonObject: {
      _id: string;
      discipline?: { name: string } | null;
      name: string;
    };
    lesson_score?: number | null;
    status: ProgressionStatus;
  }>;
  subscriptionDate?: string | null;
};

type ClassStudentsDrawerProps = {
  classId?: string | null;
  className?: string;
  isOpen: boolean;
  onClose: () => void;
  studentsCount?: number | null;
};

const GET_CLASS_STUDENTS = gql`
  query GetClassStudentsMonitoring($query: GetStudentsByClassInput!) {
    getStudentsByClass(query: $query) {
      _id
      blockedAtSomeLessons
      completedLessons
      email
      firstName
      gender
      lastName
      phoneNumber
      points
      status
      totalLessons
    }
  }
`;

const GET_CLASS_STUDENT_DETAIL = gql`
  query GetClassStudentDetailsMonitoring($query: GetStudentDetailByClassInput!) {
    getStudentDetailsByClass(query: $query) {
      _id
      birthDate
      classRanking
      firstName
      gender
      lastName
      phoneNumber
      points
      subscriptionDate
      disciplines {
        completedLessons
        points
        ranking
        totalLessons
        discipline { _id name }
        teacher { firstName lastName email }
      }
      progressions {
        lastEvaluationDate
        lesson_score
        status
        lessonObject {
          _id
          name
          discipline { name }
        }
      }
    }
  }
`;

const formatDate = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).format(new Date(value))
    : 'Non renseignee';

const formatPerson = (person?: Person | null) => {
  const fullName = [person?.firstName, person?.lastName].filter(Boolean).join(' ');
  return fullName || person?.email || 'Non assigne';
};

const formatStudent = (
  student?: Pick<StudentItem, 'email' | 'firstName' | 'lastName' | 'phoneNumber'> | null,
) => {
  const fullName = [student?.firstName, student?.lastName].filter(Boolean).join(' ');
  return fullName || student?.phoneNumber || student?.email || 'Apprenant sans nom';
};

const getStudentUsername = (student?: StudentItem | null) =>
  student?.phoneNumber || student?.email || '';

const getProgressPercent = (completed?: number, total?: number) =>
  total ? Math.min(100, Math.round(((completed ?? 0) / total) * 100)) : 0;

const studentStatusLabel = (status?: ClassRessoucesStudentStatus | null) => {
  switch (status) {
    case ClassRessoucesStudentStatus.Accepted:
      return 'Accepte';
    case ClassRessoucesStudentStatus.AwaitingForPayment:
      return 'Paiement attendu';
    case ClassRessoucesStudentStatus.Pending:
      return 'En attente';
    case ClassRessoucesStudentStatus.Rejected:
      return 'Rejete';
    case ClassRessoucesStudentStatus.Requested:
      return 'Demande';
    default:
      return 'Non renseigne';
  }
};

const progressionStatusLabel = (status?: ProgressionStatus | null) => {
  switch (status) {
    case ProgressionStatus.Completed:
      return 'Termine';
    case ProgressionStatus.InProgress:
      return 'En cours';
    case ProgressionStatus.Started:
      return 'Demarre';
    case ProgressionStatus.NotStarted:
      return 'Non demarre';
    default:
      return 'Non renseigne';
  }
};

const studentStatusTone = (
  status?: ClassRessoucesStudentStatus | null,
): 'green' | 'red' | 'yellow' | 'gray' => {
  if (status === ClassRessoucesStudentStatus.Accepted) return 'green';
  if (status === ClassRessoucesStudentStatus.Rejected) return 'red';
  if (
    status === ClassRessoucesStudentStatus.Pending ||
    status === ClassRessoucesStudentStatus.Requested ||
    status === ClassRessoucesStudentStatus.AwaitingForPayment
  ) {
    return 'yellow';
  }
  return 'gray';
};

const progressionStatusTone = (
  status?: ProgressionStatus | null,
): 'green' | 'red' | 'yellow' | 'gray' => {
  if (status === ProgressionStatus.Completed) return 'green';
  if (status === ProgressionStatus.InProgress || status === ProgressionStatus.Started) {
    return 'yellow';
  }
  return 'gray';
};

const Badge = ({
  children,
  tone,
}: {
  children: ReactNode;
  tone: 'green' | 'red' | 'yellow' | 'gray';
}) => {
  const color = {
    green: 'border-[#244A3C] bg-[#14251F] text-[#7AD7B0]',
    gray: 'border-[#343434] bg-[#202020] text-[#D0D5DD]',
    red: 'border-[#513030] bg-[#281818] text-[#FF9A9A]',
    yellow: 'border-[#4B3F20] bg-[#211D12] text-[#F5C84C]',
  }[tone];

  return (
    <span className={`rounded-[6px] border px-2.5 py-1 text-xs font-semibold ${color}`}>
      {children}
    </span>
  );
};

const ProgressBar = ({ value }: { value: number }) => (
  <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#303030]">
    <div className="h-full rounded-full bg-primary" style={{ width: `${value}%` }} />
  </div>
);

const InfoLine = ({ label, value }: { label: string; value?: ReactNode }) => (
  <div className="grid grid-cols-[132px_minmax(0,1fr)] gap-4 border-b border-[#292929] py-3.5 last:border-b-0">
    <span className="text-xs uppercase text-[#9A9A9A]">{label}</span>
    <span className="min-w-0 text-sm text-white">
      {value || 'Non renseigne'}
    </span>
  </div>
);

const ClassStudentsDrawer = ({
  classId,
  className,
  isOpen,
  onClose,
  studentsCount,
}: ClassStudentsDrawerProps) => {
  const [studentSearch, setStudentSearch] = useState('');
  const [selectedStudentUsername, setSelectedStudentUsername] = useState<string | null>(null);

  const studentsQueryInput = useMemo<GetStudentsByClassInput | null>(() => {
    if (!classId) return null;
    const value = studentSearch.trim();

    return {
      classId,
      sortBy: StudentSortBy.Points,
      ...(value ? { search: value } : {}),
    };
  }, [classId, studentSearch]);

  const studentsQuery = useQuery<StudentItem[], Error>({
    queryKey: ['classStudentsMonitoring', studentsQueryInput],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getStudentsByClass: StudentItem[] },
        { query: GetStudentsByClassInput }
      >(GET_CLASS_STUDENTS, { query: studentsQueryInput as GetStudentsByClassInput });
      return data.getStudentsByClass;
    },
    enabled: isOpen && !!studentsQueryInput,
  });

  const students = useMemo(() => studentsQuery.data ?? [], [studentsQuery.data]);
  const selectedStudent = useMemo(
    () =>
      students.find((student) => getStudentUsername(student) === selectedStudentUsername) ??
      null,
    [students, selectedStudentUsername],
  );

  useEffect(() => {
    if (!isOpen) return;
    setStudentSearch('');
    setSelectedStudentUsername(null);
  }, [classId, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!students.length) {
      setSelectedStudentUsername(null);
      return;
    }
    if (
      !selectedStudentUsername ||
      !students.some((student) => getStudentUsername(student) === selectedStudentUsername)
    ) {
      setSelectedStudentUsername(getStudentUsername(students[0]) || null);
    }
  }, [isOpen, selectedStudentUsername, students]);

  const studentDetailQueryInput = useMemo<GetStudentDetailByClassInput | null>(() => {
    if (!classId || !selectedStudentUsername) return null;
    return { classId, username: selectedStudentUsername };
  }, [classId, selectedStudentUsername]);

  const studentDetailQuery = useQuery<StudentDetail, Error>({
    queryKey: ['classStudentDetailsMonitoring', studentDetailQueryInput],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getStudentDetailsByClass: StudentDetail },
        { query: GetStudentDetailByClassInput }
      >(GET_CLASS_STUDENT_DETAIL, {
        query: studentDetailQueryInput as GetStudentDetailByClassInput,
      });
      return data.getStudentDetailsByClass;
    },
    enabled: isOpen && !!studentDetailQueryInput,
  });

  const handleStudentSearch = (
    event: ChangeEvent<HTMLInputElement> | InputMaskChangeEvent,
  ) => setStudentSearch(event.target.value || '');

  const studentDetail = studentDetailQuery.data;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      position={ModalPositions.RIGHT}
      headerStyle="bg-[#161616] h-[48px]"
      modalStyle="bg-[#161616] p-0"
    >
      <div className="flex h-[calc(100vh_-_48px)] w-[1040px] max-w-[calc(100vw_-_24px)] flex-col text-white">
        <div className="border-b border-[#2C2C2C] px-8 pb-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs uppercase text-[#9A9A9A]">Monitoring apprenants</p>
              <h2 className="mt-1 text-2xl font-semibold text-white">Apprenants</h2>
              <p className="mt-1 text-sm text-[#9A9A9A]">{className || 'Classe'}</p>
            </div>
            <Badge tone="gray">{students.length}/{studentsCount ?? 0}</Badge>
          </div>
          <div className="mt-5">
            <SearchBar
              placeholder="Rechercher un apprenant"
              value={studentSearch}
              handleChange={handleStudentSearch}
              className="!w-full"
            />
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 overflow-y-auto lg:grid-cols-[minmax(340px,0.82fr)_minmax(520px,1.18fr)] lg:overflow-hidden">
          <div className="min-h-0 border-b border-[#2C2C2C] p-6 lg:border-b-0 lg:border-r">
            {studentsQuery.error ? (
              <div className="rounded-[8px] bg-[#3A1717] p-3 text-sm text-[#FFB4B4]">
                {studentsQuery.error.message}
              </div>
            ) : null}
            {studentsQuery.isLoading ? (
              <div className="flex h-full items-center justify-center text-primary">
                <i className="pi pi-spin pi-spinner text-2xl" />
              </div>
            ) : (
              <div className="h-full space-y-3 overflow-y-auto pr-1">
                {students.length ? (
                  students.map((student) => {
                    const username = getStudentUsername(student);
                    const isSelected = username && username === selectedStudentUsername;
                    const progressPercent = getProgressPercent(
                      student.completedLessons,
                      student.totalLessons,
                    );

                    return (
                      <button
                        className={`w-full rounded-[8px] border p-4 text-left transition ${
                          isSelected
                            ? 'border-[#5A4719] bg-[#211D12]'
                            : 'border-[#2C2C2C] bg-[#1A1A1A] hover:border-[#4B3F20] hover:bg-[#202020]'
                        }`}
                        disabled={!username}
                        key={student._id || username || formatStudent(student)}
                        onClick={() => setSelectedStudentUsername(username)}
                        type="button"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate font-semibold text-white">
                              {formatStudent(student)}
                            </p>
                            <p className="mt-1 truncate text-xs text-[#939393]">
                              {student.phoneNumber || student.email || 'Contact non renseigne'}
                            </p>
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-1">
                            <Badge tone={studentStatusTone(student.status)}>
                              {studentStatusLabel(student.status)}
                            </Badge>
                            {student.blockedAtSomeLessons ? (
                              <Badge tone="red">Bloque</Badge>
                            ) : null}
                          </div>
                        </div>
                        <div className="mt-3 flex items-center justify-between text-xs text-[#D0D5DD]">
                          <span>{student.completedLessons}/{student.totalLessons} lecons</span>
                          <span>{progressPercent}%</span>
                        </div>
                        <ProgressBar value={progressPercent} />
                        <p className="mt-3 text-xs text-[#9A9A9A]">{student.points} points</p>
                      </button>
                    );
                  })
                ) : (
                  <p className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4 text-sm text-[#9A9A9A]">
                    Aucun apprenant trouve
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="min-h-0 overflow-y-auto p-6">
            {!selectedStudent && !studentsQuery.isLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-[#939393]">
                Selectionne un apprenant
              </div>
            ) : null}

            {selectedStudent ? (
              <div className="space-y-6">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs uppercase text-[#9A9A9A]">Profil apprenant</p>
                    <h3 className="mt-1 text-2xl font-semibold text-white">
                      {formatStudent(selectedStudent)}
                    </h3>
                    <p className="mt-1 text-sm text-[#9A9A9A]">
                      Detail progression apprenant
                    </p>
                  </div>
                  <Badge tone={studentStatusTone(selectedStudent.status)}>
                    {studentStatusLabel(selectedStudent.status)}
                  </Badge>
                </div>

                {studentDetailQuery.error ? (
                  <div className="rounded-[8px] bg-[#3A1717] p-3 text-sm text-[#FFB4B4]">
                    {studentDetailQuery.error.message}
                  </div>
                ) : null}
                {studentDetailQuery.isLoading ? (
                  <div className="flex items-center justify-center py-8 text-primary">
                    <i className="pi pi-spin pi-spinner text-2xl" />
                  </div>
                ) : null}

                {studentDetail && !studentDetailQuery.isLoading ? (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4">
                        <p className="text-xs uppercase text-[#9A9A9A]">Rang</p>
                        <p className="mt-1 text-2xl font-semibold">#{studentDetail.classRanking}</p>
                      </div>
                      <div className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4">
                        <p className="text-xs uppercase text-[#9A9A9A]">Points</p>
                        <p className="mt-1 text-2xl font-semibold">{studentDetail.points}</p>
                      </div>
                      <div className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4">
                        <p className="text-xs uppercase text-[#9A9A9A]">Progressions</p>
                        <p className="mt-1 text-2xl font-semibold">
                          {studentDetail.progressions.length}
                        </p>
                      </div>
                    </div>

                    <div className="border-y border-[#292929] py-2">
                      <InfoLine label="Telephone" value={studentDetail.phoneNumber} />
                      <InfoLine label="Genre" value={studentDetail.gender || 'Non renseigne'} />
                      <InfoLine
                        label="Inscription"
                        value={formatDate(studentDetail.subscriptionDate)}
                      />
                      <InfoLine
                        label="Naissance"
                        value={studentDetail.birthDate || 'Non renseignee'}
                      />
                    </div>

                    <div>
                      <h4 className="mb-3 text-xs font-semibold uppercase text-[#9A9A9A]">
                        Progression par matiere
                      </h4>
                      <div className="space-y-3">
                        {studentDetail.disciplines.length ? (
                          studentDetail.disciplines.map((item) => {
                            const percent = getProgressPercent(
                              item.completedLessons,
                              item.totalLessons,
                            );

                            return (
                              <div className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4" key={item.discipline._id}>
                                <div className="flex items-start justify-between gap-3">
                                  <div>
                                    <p className="font-semibold">{item.discipline.name}</p>
                                    <p className="mt-1 text-xs text-[#9A9A9A]">
                                      {formatPerson(item.teacher)}
                                    </p>
                                  </div>
                                  <span className="text-sm text-primary">{percent}%</span>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-xs text-[#D0D5DD]">
                                  <span>{item.completedLessons}/{item.totalLessons} lecons</span>
                                  <span>#{item.ranking} - {item.points} pts</span>
                                </div>
                                <ProgressBar value={percent} />
                              </div>
                            );
                          })
                        ) : (
                          <p className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4 text-sm text-[#9A9A9A]">
                            Aucune matiere suivie
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <h4 className="mb-3 text-xs font-semibold uppercase text-[#9A9A9A]">
                        Dernieres progressions
                      </h4>
                      <div className="space-y-3">
                        {studentDetail.progressions.length ? (
                          studentDetail.progressions.slice(0, 5).map((item) => (
                            <div
                              className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4"
                              key={`${item.lessonObject._id}-${item.lastEvaluationDate || item.status}`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div>
                                  <p className="font-semibold">{item.lessonObject.name}</p>
                                  <p className="mt-1 text-xs text-[#9A9A9A]">
                                    {item.lessonObject.discipline?.name ||
                                      'Discipline non renseignee'}
                                  </p>
                                </div>
                                <Badge tone={progressionStatusTone(item.status)}>
                                  {progressionStatusLabel(item.status)}
                                </Badge>
                              </div>
                              <div className="mt-2 flex items-center justify-between text-xs text-[#D0D5DD]">
                                <span>Score {item.lesson_score ?? 0}</span>
                                <span>{formatDate(item.lastEvaluationDate)}</span>
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="rounded-[8px] border border-[#2C2C2C] bg-[#1A1A1A] p-4 text-sm text-[#9A9A9A]">
                            Aucune progression detaillee
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ClassStudentsDrawer;
